import { useEffect, useRef, useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Upload, ExternalLink, ImageOff, Users } from "lucide-react";

// Project Page Redesign (2026-02), B1+B2 — staff-facing "Behind the Scenes" gallery under
// Deliverables, wired onto the existing `modules/client_bts.py` backend (list/upload/patch/
// delete, ~80% already built). Storage now resolves the project's OWN canonical Drive folder
// (same fix as the Resource Link/SOW), and uploads go through the same real, content-based
// EXIF-strip pipeline as Community's own uploads (`kernel/image_pipeline.py`). Per-image
// "Visible to client" toggle drives the SAME `client_visible` field the Client Portal's own
// BtsGallery.jsx already reads — flipping it here shows/hides the image there immediately, no
// separate publish step. Deliberately scoped to B1+B2 only — partner/freelancer upload access
// and the "Post to Floor" bridge action are explicit follow-ups (B3/B4), not built this pass.
//
// Agency Portal Restyle Wave 1 (2026-10) — `agency_visible` is a SECOND, independent flag on
// the same row (never tied to `client_visible`). Deliberately a multi-select + confirm step
// (not an instant toggle like the client one) per the design — sharing with an agency is a
// bigger, more deliberate action than the internal client-visibility flip. Un-sharing a single
// photo stays instant (no confirm needed to walk something back).
// Regression v6 fix pass (2026-10), Part 3 — `mode`: "full" (default, the staff Project page's
// own Communication-tab gallery: upload + client-visibility + agency-visibility) or "agency"
// (embedded inside AgencyWorkspaceManager, the ONE shared agency-management component used by
// both the Project page and Partners > {agency}) — same component, same endpoints, no second
// copy: upload/Drive/client-visibility are dropped so the only thing it can change there is
// what the AGENCY sees.
export function ProjectBtsGallery({ projectId, driveUrl, mode = "full" }) {
  const agencyOnly = mode === "agency";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  // "share" or "unshare" — which bulk action the confirm modal is about (Part A, 2026-10:
  // "Select all" + a bulk Share/Stop-sharing action, not just Share).
  const [confirming, setConfirming] = useState(null);
  const [sharing, setSharing] = useState(false);
  const fileInputRef = useRef(null);

  const load = () => api.get(`/v1/client-portal/admin/projects/${projectId}/bts-media`).then((r) => setItems(r.data.items || [])).catch(() => setItems([])).finally(() => setLoading(false));

  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      form.append("client_visible", "false");
      await api.post(`/v1/client-portal/admin/projects/${projectId}/bts-media`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(files.length > 1 ? `${files.length} photos uploaded — hidden from client by default` : "Uploaded — hidden from client by default");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = detail?.code;
      if (code === "project_missing_drive_folder") toast.error("Link a Drive folder for this project first (Workspace tab → Project Files)");
      else if (code === "drive_not_configured") toast.error("Drive isn't connected yet");
      else if (code === "too_many_files") toast.error(`Upload up to ${detail.max_files} files at a time`);
      else if (detail?.allowed) toast.error("Only JPEG, PNG, WEBP, HEIC images or MP4 clips");
      else if (code === "drive_folder_not_accessible") toast.error(detail.hint || "This project's Drive folder isn't shared with the app — ask a founder to check Admin → Drive Health", { duration: 12000 });
      else if (detail?.error) toast.error(`Upload failed: ${detail.error}`, { duration: 8000 });
      else toast.error("Couldn't upload this file");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const [dragActive, setDragActive] = useState(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    upload(e.dataTransfer?.files);
  };

  const toggleVisible = async (media, next) => {
    setItems((prev) => prev.map((m) => (m.id === media.id ? { ...m, client_visible: next } : m)));
    try {
      await api.patch(`/v1/client-portal/admin/projects/${projectId}/bts-media/${media.id}`, { client_visible: next });
    } catch {
      toast.error("Couldn't update visibility");
      setItems((prev) => prev.map((m) => (m.id === media.id ? { ...m, client_visible: !next } : m)));
    }
  };

  // Regression v6 fix pass (2026-10), Part 1 item 1 — a REAL per-photo "Agency" toggle beside
  // "Visible to client" (both directions, no confirm), independent of `client_visible`. Before
  // this, the only per-photo affordance was an unlabelled select circle + a bulk action that
  // appeared once something was selected, which read as "there is no agency toggle".
  const toggleAgencyVisible = async (media, next) => {
    setItems((prev) => prev.map((m) => (m.id === media.id ? { ...m, agency_visible: next } : m)));
    try {
      await api.patch(`/v1/client-portal/admin/projects/${projectId}/bts-media/${media.id}`, { agency_visible: next });
    } catch {
      toast.error("Couldn't update visibility");
      setItems((prev) => prev.map((m) => (m.id === media.id ? { ...m, agency_visible: !next } : m)));
    }
  };

  const toggleSelect = (mediaId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(mediaId)) next.delete(mediaId); else next.add(mediaId);
      return next;
    });
  };

  // Part A (2026-10): selectable = every non-video item, regardless of current agency_visible
  // state — a mixed selection of shared + unshared items is fine, the bulk action below just
  // sets every selected id to the SAME agency_visible value.
  const selectableIds = items.filter((m) => !(m.mime_type || "").startsWith("video/")).map((m) => m.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(selectableIds));

  const confirmBulkAgencyVisibility = async (nextVisible) => {
    setSharing(true);
    try {
      const mediaIds = Array.from(selected);
      await api.post(`/v1/client-portal/admin/projects/${projectId}/bts-media/bulk-agency-visibility`, { media_ids: mediaIds, agency_visible: nextVisible });
      setItems((prev) => prev.map((m) => (mediaIds.includes(m.id) ? { ...m, agency_visible: nextVisible } : m)));
      toast.success(nextVisible
        ? `Shared ${mediaIds.length} photo${mediaIds.length === 1 ? "" : "s"} with the agency`
        : `Stopped sharing ${mediaIds.length} photo${mediaIds.length === 1 ? "" : "s"} with the agency`);
      setSelected(new Set());
      setConfirming(null);
    } catch {
      toast.error(nextVisible ? "Couldn't share with the agency" : "Couldn't stop sharing with the agency");
    } finally {
      setSharing(false);
    }
  };

  const remove = async (media) => {
    setItems((prev) => prev.filter((m) => m.id !== media.id));
    try {
      await api.delete(`/v1/client-portal/admin/projects/${projectId}/bts-media/${media.id}`);
    } catch {
      toast.error("Couldn't remove this image");
      load();
    }
  };

  const visibleCount = items.filter((m) => m.client_visible).length;
  const agencyVisibleCount = items.filter((m) => m.agency_visible).length;

  return (
    <div className="rounded-[10px] border border-[#E5E6EC] bg-white" data-testid="project-bts-gallery">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-[#E5E6EC] flex-wrap">
        <span className="font-editorial text-[15px] uppercase tracking-[0.12em]">{agencyOnly ? "Behind the scenes · what the agency sees" : "Behind the Scenes"}</span>
        <span className="text-[11px] text-[#9BA1A4]">
          {items.length === 0
            ? "Nothing uploaded yet"
            : agencyOnly
              ? `${agencyVisibleCount} of ${items.length} shared with agency`
              : `${visibleCount} of ${items.length} visible to client · ${agencyVisibleCount} shared with agency · stored in this project's Drive folder`}
        </span>
        <div className="ml-auto flex gap-2 text-[12.5px] font-semibold">
          {selectableIds.length > 0 && (
            <button onClick={toggleSelectAll} data-testid="project-bts-select-all-btn"
              className="inline-flex items-center gap-1.5 rounded-[7px] border border-[#E5E6EC] px-3 py-1.5 text-[#495053] hover:border-[#111213]">
              {allSelected ? "Clear selection" : "Select all"}
            </button>
          )}
          {selected.size > 0 && (
            <>
              <button onClick={() => setConfirming("share")} data-testid="project-bts-share-agency-btn"
                className="inline-flex items-center gap-1.5 rounded-[7px] bg-[#111213] px-3 py-1.5 text-white hover:bg-[#2A2A2A]">
                <Users className="h-3 w-3" /> Share {selected.size} with agency
              </button>
              <button onClick={() => setConfirming("unshare")} data-testid="project-bts-unshare-agency-bulk-btn"
                className="inline-flex items-center gap-1.5 rounded-[7px] border border-[#E5E6EC] px-3 py-1.5 text-[#495053] hover:border-[#111213]">
                Stop sharing {selected.size}
              </button>
            </>
          )}
          {driveUrl && !agencyOnly && (
            <a href={driveUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-[7px] border border-[#E5E6EC] px-3 py-1.5 hover:border-[#FFC817]" data-testid="project-bts-open-drive-btn">
              Open in Drive <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {!agencyOnly && (
            <>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4" multiple className="hidden" data-testid="project-bts-upload-input" onChange={(e) => upload(e.target.files)} />
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="project-bts-upload-btn"
                className="inline-flex items-center gap-1.5 rounded-[7px] bg-[#FFC817] px-3 py-1.5 text-[#111213] hover:bg-[#FFD447] disabled:opacity-50">
                <Upload className="h-3 w-3" /> {uploading ? "Uploading…" : "Upload images"}
              </button>
            </>
          )}
        </div>
      </div>
      {confirming && (
        <div className="fixed inset-0 z-[200] bg-black/55 flex items-center justify-center p-6" data-testid="project-bts-share-confirm-modal">
          <div className="bg-white rounded-[10px] max-w-[420px] w-full p-6">
            <div className="font-editorial text-[16px] mb-2">
              {confirming === "share" ? `Share ${selected.size} photo${selected.size === 1 ? "" : "s"} with the agency?` : `Stop sharing ${selected.size} photo${selected.size === 1 ? "" : "s"} with the agency?`}
            </div>
            <p className="text-[13px] text-[#495053] mb-5">
              {confirming === "share"
                ? "They'll appear in the project's Behind the Scenes section on the Agency Portal, visible to everyone on the agency side — including any caption."
                : "They'll disappear from the project's Behind the Scenes section on the Agency Portal immediately."}
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirming(null)} data-testid="project-bts-share-confirm-cancel" className="rounded-[7px] border border-[#E5E6EC] px-4 py-2 text-[13px] font-semibold hover:border-[#111213]">Cancel</button>
              <button onClick={() => confirmBulkAgencyVisibility(confirming === "share")} disabled={sharing} data-testid="project-bts-share-confirm-ok" className="rounded-[7px] bg-[#FFC817] px-4 py-2 text-[13px] font-semibold text-[#111213] hover:bg-[#FFD447] disabled:opacity-50">
                {sharing ? "Working…" : confirming === "share" ? "Share with agency" : "Stop sharing"}
              </button>
            </div>
          </div>
        </div>
      )}
      {loading ? (
        <div className="text-center text-xs text-[#9BA1A4] py-4">Loading…</div>
      ) : items.length === 0 ? (
        agencyOnly ? (
          <div className="px-5 py-3 text-[12.5px] text-[#9BA1A4]" data-testid="project-bts-agency-empty">
            No BTS uploaded on this project yet — upload stills from the project's Communication tab, then share them here.
          </div>
        ) : (
        // Project Workspace Phase 1 (2026-09): empty = one compact row, not the full 170px
        // dropzone grid — the upload button in the header above still opens the file picker.
        <div
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          data-testid="project-bts-dropzone"
          className={`px-5 py-3 text-[12.5px] transition-colors ${dragActive ? "bg-[#FFFBEF] text-[#495053]" : "text-[#9BA1A4]"}`}>
          No BTS uploaded yet — drop stills here or use Upload images above.
        </div>
        )
      ) : (
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3.5">
          {items.map((m) => (
            <BtsCard key={m.id} projectId={projectId} media={m} onToggle={toggleVisible} onDelete={remove}
              selected={selected.has(m.id)} onSelect={() => toggleSelect(m.id)} onToggleAgency={toggleAgencyVisible} agencyOnly={agencyOnly} />
          ))}
          {!agencyOnly && (
          <button
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            data-testid="project-bts-more-dropzone"
            className={`rounded-[9px] border-[1.5px] border-dashed flex flex-col items-center justify-center gap-2 text-xs min-h-[170px] transition-colors ${dragActive ? "border-[#FFC817] text-[#495053] bg-[#FFFBEF]" : "border-[#D8D9DE] text-[#9BA1A4] hover:border-[#FFC817] hover:text-[#495053]"}`}>
            <Upload className="h-5 w-5" />
            Drop stills here (or select multiple)
            <span className="text-[10.5px]">Hidden from client by default</span>
          </button>
          )}
        </div>
      )}
    </div>
  );
}

const BtsCard = ({ projectId, media, onToggle, onDelete, selected, onSelect, onToggleAgency, agencyOnly = false }) => {
  const [objectUrl, setObjectUrl] = useState(null);
  const [imgError, setImgError] = useState(false);
  const isVideo = (media.mime_type || "").startsWith("video/");

  useEffect(() => {
    let url;
    let active = true;
    api.get(`/v1/client-portal/admin/projects/${projectId}/bts-media/${media.id}/file`, { responseType: "blob" }).then((r) => {
      if (!active) return;
      url = window.URL.createObjectURL(r.data);
      setObjectUrl(url);
    }).catch(() => setImgError(true));
    return () => { active = false; if (url) window.URL.revokeObjectURL(url); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, media.id]);

  return (
    <div className="rounded-[9px] border border-[#E5E6EC] overflow-hidden bg-white" data-testid={`project-bts-card-${media.id}`}>
      <div className="aspect-[4/3] bg-[#EDDBCC] flex items-center justify-center overflow-hidden relative">
        {!isVideo && (
          <button onClick={onSelect} data-testid={`project-bts-select-${media.id}`} aria-label="Select for bulk agency sharing"
            className={`absolute top-2 left-2 z-10 w-[22px] h-[22px] rounded-full border-2 border-white cursor-pointer ${selected ? "bg-[#FFC817]" : "bg-black/45"}`} />
        )}
        {!objectUrl ? (
          imgError ? <ImageOff className="h-5 w-5 text-[#A7907C]" /> : <div className="text-[10px] text-[#A7907C]">Loading…</div>
        ) : isVideo ? (
          <video src={objectUrl} className="h-full w-full object-cover" muted />
        ) : (
          <img src={objectUrl} alt={media.caption || "BTS"} className="h-full w-full object-cover" onError={() => setImgError(true)} loading="lazy" decoding="async" />
        )}
      </div>
      <div className="p-3 flex flex-col gap-2.5">
        {!agencyOnly && (
          <button onClick={() => onToggle(media, !media.client_visible)} data-testid={`project-bts-visible-toggle-${media.id}`}
            className="flex items-center gap-2 text-left">
            <span className={`w-8 h-[18px] rounded-full flex items-center px-0.5 transition-colors ${media.client_visible ? "bg-[#0F7A55] justify-end" : "bg-[#D8D9DE] justify-start"}`}>
              <span className="w-3.5 h-3.5 rounded-full bg-white shadow" />
            </span>
            <span className={`text-[11.5px] font-semibold ${media.client_visible ? "text-[#0F7A55]" : "text-[#495053]"}`}>Visible to client</span>
          </button>
        )}
        {isVideo ? (
          <span className="flex items-center gap-1.5 text-[11.5px] text-[#9BA1A4]"><Users className="h-3 w-3" /> Clips are never shared with the agency</span>
        ) : (
          <button onClick={() => onToggleAgency(media, !media.agency_visible)} data-testid={`project-bts-agency-toggle-${media.id}`}
            aria-pressed={!!media.agency_visible} className="flex items-center gap-2 text-left">
            <span className={`w-8 h-[18px] rounded-full flex items-center px-0.5 transition-colors ${media.agency_visible ? "bg-[#8A6A00] justify-end" : "bg-[#D8D9DE] justify-start"}`}>
              <span className="w-3.5 h-3.5 rounded-full bg-white shadow" />
            </span>
            <span className={`text-[11.5px] font-semibold ${media.agency_visible ? "text-[#8A6A00]" : "text-[#495053]"}`}>
              <Users className="h-3 w-3 inline mr-1" />Shared with agency
            </span>
          </button>
        )}
        <div className="flex items-center justify-between text-[11px] text-[#9BA1A4]">
          <span>{media.uploaded_by?.split("@")[0] || "—"}</span>
          {!agencyOnly && (
            <button onClick={() => onDelete(media)} data-testid={`project-bts-delete-btn-${media.id}`} className="font-semibold text-[#495053] hover:text-[#B42318]">Remove</button>
          )}
        </div>
      </div>
    </div>
  );
};
